/**
 * Cancel Campaign hook.
 *
 * Manages the lifecycle of a single cancel-campaign API call:
 *   Idle → In-flight → Failure-key set | Success
 *
 * Each call to `executeCancellation` resets stale state from prior attempts.
 * The accessor layer adds the CSRF token; the hook adds a concurrency guard, a mounted-ref guard against post-unmount setState, and surfaces a stable
 * error *key* (not pre-translated text) so consumers can localize at render time — this avoids freezing in an empty string when the i18n bundle is
 * still loading at the moment the API call lands.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { CampaignType, OperationStatus } from 'src/@types/valueMatching/enums';
import { cancelCampaign } from 'src/accessors/valueMatchingAccessor';
import { logError } from 'src/utils/monitoring';

/** Stable error keys returned by the hook. Consumers translate at render time. */
export const CANCEL_CAMPAIGN_ERROR_KEY = 'cancel_campaign_failed';
export type CancelCampaignErrorKey = typeof CANCEL_CAMPAIGN_ERROR_KEY;

export interface UseCancelCampaignResult {
    /**
     * Triggers the cancel-campaign API call. Resets `errorKey` and `success`
     * state before the request and updates them based on the response.
     */
    executeCancellation: (
        campaignId: string,
        version: number,
        campaignType: CampaignType,
    ) => Promise<void>;
    /** True while the cancel-campaign API call is in-flight. */
    cancelling: boolean;
    /**
     * Stable failure key — `null` on success / idle. Consumer translates at
     * render time so the string follows late-loading i18n bundles correctly.
     */
    errorKey: CancelCampaignErrorKey | null;
    /** True after a successful cancellation; `false` initially and on failure. */
    success: boolean;
    /** Resets `errorKey` / `success` state — used when the modal is dismissed. */
    reset: () => void;
}

/**
 * Hook that manages the cancel-campaign API call lifecycle.
 *
 * Returns:
 *   - `cancelling`  — true while the request is in flight
 *   - `errorKey`    — stable string key on failure, otherwise null
 *   - `success`     — true on `OperationStatus.SUCCESS`
 *   - `executeCancellation` — fires the request
 *   - `reset`       — clears `errorKey` / `success` (e.g. when dismissing the modal)
 */
export function useCancelCampaign(): UseCancelCampaignResult {
    const [cancelling, setCancelling] = useState(false);
    const [errorKey, setErrorKey] = useState<CancelCampaignErrorKey | null>(null);
    const [success, setSuccess] = useState(false);

    // Concurrency guard — prevents duplicate POSTs from rapid double-clicks.
    const inFlightRef = useRef(false);

    // Mounted guard — protects against React warnings when the consumer unmounts
    // (e.g. ViewCampaignPage navigates away on success) before this Promise
    // settles. All state updates after `await` are gated on `mountedRef.current`.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const reset = useCallback(() => {
        setErrorKey(null);
        setSuccess(false);
    }, []);

    const executeCancellation = useCallback(
        async (campaignId: string, version: number, campaignType: CampaignType) => {
            if (inFlightRef.current) return;
            // Wrap the entire body — including the pre-await synchronous setup —
            // in one try/finally so the concurrency guard is always released even
            // if a synchronous throw slips between the ref flip and the try.
            inFlightRef.current = true;
            try {
                if (mountedRef.current) {
                    setCancelling(true);
                    setErrorKey(null);
                    setSuccess(false);
                }

                try {
                    const response = await cancelCampaign({ campaignId, version, campaignType });
                    if (!mountedRef.current) return;

                    if (response.operationStatus === OperationStatus.SUCCESS) {
                        setSuccess(true);
                    } else {
                        setErrorKey(CANCEL_CAMPAIGN_ERROR_KEY);
                    }
                } catch (err) {
                    const message = err instanceof Error ? err.message : String(err);
                    logError(message, { methodName: 'useCancelCampaign', campaignId, version });
                    if (mountedRef.current) {
                        setErrorKey(CANCEL_CAMPAIGN_ERROR_KEY);
                    }
                }
            } finally {
                inFlightRef.current = false;
                if (mountedRef.current) {
                    setCancelling(false);
                }
            }
        },
        [],
    );

    return { executeCancellation, cancelling, errorKey, success, reset };
}
